import { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Avatar,
    Chip,
    useTheme,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    AccessTime as AccessTimeIcon,
    Email as EmailIcon,
    CardMembership as MembershipIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Payment as PaymentIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useResponsive } from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshUser();
            const response = await api.get('/auth/me');
            setProfileData(response.data.user);
        } catch (error) {
            console.log('Error refreshing profile data');
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const response = await api.get('/auth/me');
                setProfileData(response.data.user);
            } catch (error) {
                console.log('Error fetching profile data');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const hasActiveMembership = profileData?.membershipNumber && 
                               profileData?.membershipType && 
                               profileData?.membershipClassification &&
                               profileData?.membershipContractStatus == 'Active';

    return (
        <Box>
            <Box display="flex" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Profile
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton 
                        onClick={handleRefresh} 
                        sx={{ ml: 1 }}
                        disabled={refreshing}
                    >
                        <RefreshIcon 
                            sx={{ 
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': {
                                        transform: 'rotate(0deg)',
                                    },
                                    '100%': {
                                        transform: 'rotate(360deg)',
                                    },
                                },
                            }} 
                        />
                    </IconButton>
                </Tooltip>
            </Box>
            <Grid container spacing={3}>
                {/* Basic Info Card */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                            <Avatar
                                src={profileData?.memberPhotoUrl}
                                sx={{
                                    width: isMobile ? 120 : 150,
                                    height: isMobile ? 120 : 150,
                                    mb: 2,
                                    border: `4px solid ${theme.palette.primary.main}`
                                }}
                            >
                                {profileData?.firstName?.[0]}{profileData?.lastName?.[0]}
                            </Avatar>
                            <Typography variant="h5" gutterBottom>
                                {profileData?.firstName} {profileData?.lastName}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                {profileData?.ssUsername}
                            </Typography>
                            <Chip
                                icon={<CheckCircleIcon />}
                                label={profileData?.active ? 'Active' : 'Inactive'}
                                color={profileData?.active ? 'success' : 'error'}
                                sx={{ mt: 1 }}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Account Details */}
                <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <PersonIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Account Information</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                Client ID
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {profileData?.ezFacilityClientId}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                Gender
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {profileData?.gender?.charAt(0).toUpperCase() + profileData?.gender?.slice(1) || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {profileData?.email?.[0] || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Membership Information */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <MembershipIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Membership Information</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    {!hasActiveMembership ? (
                                        <Box 
                                            display="flex" 
                                            flexDirection="column" 
                                            alignItems="center" 
                                            textAlign="center"
                                            py={2}
                                        >
                                            <WarningIcon 
                                                sx={{ 
                                                    fontSize: 48, 
                                                    color: theme.palette.warning.main,
                                                    mb: 2
                                                }} 
                                            />
                                            <Typography variant="h6" gutterBottom>
                                                Administrative Fees Unpaid
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Please visit the fees page to pay your administrative fees.
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<PaymentIcon />}
                                                onClick={() => navigate('/fees')}
                                                sx={{ mt: 2 }}
                                            >
                                                Pay Administrative Fees
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Membership Number
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipNumber}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Type
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipType}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Membership Classification
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipClassification}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Contract Status
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipContractStatus}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Visits Remaining
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipVisitsRemaining}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Visits
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {profileData?.membershipVisitsCount}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Start Date
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {formatDate(profileData?.membershipStartDate)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    End Date
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    {formatDate(profileData?.membershipEndDate)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Amount Due
                                                </Typography>
                                                <Typography variant="body1" gutterBottom>
                                                    ${profileData?.amountDue?.toFixed(2) || '0.00'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Activity Timeline */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Account Activity</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CalendarIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Account Created"
                                                secondary={formatDateTime(profileData?.dateCreated)}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CalendarIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Last Modified"
                                                secondary={formatDateTime(profileData?.lastModified)}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CalendarIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Last Check-In"
                                                secondary={formatDateTime(profileData?.lastCheckIn)}
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profile; 